/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { memo, useState, useEffect, useMemo } from 'react';
import { enableES5 } from 'immer';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import classNames from 'classnames';
import utils from '../../../utils/utils';
import { showNotification } from '../../../redux/notification';
import { user } from '../../../redux/user';
import ConfirmationModalNew from '../ConfirmationModalNew';
import facilityInfoIcon from '../../../assets/images/facility-info-icon.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/images/refresh-purple.svg';
import useShiftsForm from '../../../hooks/useShiftsForm';
import useMediaQuery, { MEDIUM_SCREEN_WIDTH_QUERY } from '../../hooks/useMediaQuery';
import PERMISSION from '../../../constants/Permissions';
import RequestNewShiftsTab from './RequestNewShiftsTab/RequestNewShiftsTab';
import RequestShiftsConfirmation from './RequestNewShiftsTab/RequestShiftsConfirmation';
import useRequestShiftsSubmit from './RequestNewShiftsTab/hooks/useRequestShiftsSubmit';
import BulkAssignShiftsTab from './BulkAssignShiftsTab/BulkAssignShiftsTab';
import BulkAssignConfirmation from './BulkAssignShiftsTab/BulkAssignConfirmation';
import useBulkAssignSubmit from './BulkAssignShiftsTab/hooks/useBulkAssignSubmit';

import './SelectFacilityRequestShiftsModal.css';

const TAB_REQUEST_NEW_SHIFTS = 'request';
const TAB_BULK_ASSIGN_SHIFTS = 'bulk';
const BULK_ASSIGN_TAB_PERMISSIONS = [PERMISSION.FLEX_ADD_EDIT_SHIFT, PERMISSION.FLEX_ADD_CAREGIVER];

enableES5();

const SelectFacilityRequestShiftsModal = (props) => {
	const userData = useSelector(user);
	const { permissions, permissionsByFacility } = userData;
	const dispatch = useDispatch();

	const isMobile = useMediaQuery(MEDIUM_SCREEN_WIDTH_QUERY);
	const [selectedFacility, setSelectedFacility] = useState();
	const [showChangingFacilityModal, setShowChangingFacilityModal] = useState(false);
	const [showChangingCaregiverModal, setShowChangingCaregiverModal] = useState(false);
	const [showChangingTabModal, setShowChangingTabModal] = useState(false);
	const [showSwitchToRecurringModal, setShowSwitchToRecurringModal] = useState(false);
	const [tabToLoad, setTabToLoad] = useState(null);
	const [needMoreShifts, setNeedMoreShifts] = useState(false);
	const [facilityToLoad, setFacilityToLoad] = useState();
	const [caregiverToLoad, setCaregiverToLoad] = useState(null);
	const [currentShiftStructures, setCurrentShiftStructures] = useState([]);
	const [currentFacilityUnits, setCurrentFacilityUnits] = useState([]);
	const [activeTab, setActiveTab] = useState(TAB_REQUEST_NEW_SHIFTS);
	const [selectedCaregiver, setSelectedCaregiver] = useState(null);

	const { shiftStructures, isGlobalCalendar } = props;

	// Callers (GlobalOverviewScreen, ShiftsScreen) pre-filter `facilities` to the flex-eligible set
	// for the Flex flow. We only need to layer the extra FLEX_ADD_CAREGIVER permission on top of the
	// implicit FLEX_ADD_EDIT_SHIFT already enforced by the caller.
	const showBulkAssignTab =
		!!props.isFlex &&
		utils.hasPermsAtAnyFacility(BULK_ASSIGN_TAB_PERMISSIONS, permissionsByFacility, props.facilities || []);

	const isOnRequestTab = activeTab === TAB_REQUEST_NEW_SHIFTS || !showBulkAssignTab;

	const allowPastDates = useMemo(() => {
		if (!props.isFlex || !selectedFacility || !selectedFacility.managementCompanyId) {
			return false;
		}

		const managementCompanyPermissions =
			(userData.managementCompanyPermissions &&
				userData.managementCompanyPermissions[selectedFacility.managementCompanyId]) ||
			[];

		return managementCompanyPermissions.includes(PERMISSION.PORTAL_CREATE_FLEX_PAST_SHIFTS);
	}, [props.isFlex, selectedFacility, userData.managementCompanyPermissions]);

	const {
		initialShiftGroups,
		shiftsGroups,
		setShiftsGroups,
		errors,
		handleBackendErrors,
		updateShift,
		removeShift,
		addEmptyShiftGroup,
		checkIntegrity,
		checkIfCanAddTodayShiftForStructure,
		reset
	} = useShiftsForm(userData, currentShiftStructures);

	const requestSubmit = useRequestShiftsSubmit({
		selectedFacility,
		shiftsGroups,
		isFlex: props.isFlex,
		isHybrid: props.isHybrid,
		isOnCall: props.isOnCall,
		allowPastDates,
		needMoreShifts,
		reset,
		handleBackendErrors,
		checkIntegrity,
		setIsLoading: props.setIsLoading,
		updateFeed: props.updateFeed,
		onClose: props.onClose,
		confirmLabel: props.confirmLabel
	});

	const bulkSubmit = useBulkAssignSubmit({
		selectedFacility,
		selectedCaregiver,
		shiftsGroups,
		shiftStructures: currentShiftStructures,
		isOnRequestTab,
		reset,
		needMoreShifts,
		handleBackendErrors,
		checkIntegrity,
		setIsLoading: props.setIsLoading,
		updateFeed: props.updateFeed,
		onClose: props.onClose
	});

	const requestTabHasContent = () => shiftsGroups.some((s) => !!s.resourceType);

	const selectTab = (nextTab) => {
		if (activeTab === nextTab) {
			return;
		}
		if (requestTabHasContent()) {
			setTabToLoad(nextTab);
			setShowChangingTabModal(true);
		} else {
			setActiveTab(nextTab);
		}
	};

	const selectRequestTab = () => selectTab(TAB_REQUEST_NEW_SHIFTS);
	const selectBulkTab = () => {
		if (selectedFacility) {
			const facilityHasAddCaregiverPermission = ((permissionsByFacility ?? {})[selectedFacility.id] ?? []).includes(
				PERMISSION.FLEX_ADD_CAREGIVER
			);
			if (!facilityHasAddCaregiverPermission) {
				setSelectedFacility(null);
			}
		}
		selectTab(TAB_BULK_ASSIGN_SHIFTS);
	};

	const handleChangeTab = () => {
		setShiftsGroups(initialShiftGroups);
		setSelectedCaregiver(null);
		if (tabToLoad) {
			setActiveTab(tabToLoad);
		}
		setTabToLoad(null);
		setShowChangingTabModal(false);
	};

	const triggerRecurringShiftsModal = () => {
		if (props.user.isRecurringScheduleEnabled) {
			props.onClose();
			props.setShowRecurringShiftsModal(true);
		} else {
			dispatch(
				showNotification(
					'error',
					'This feature is coming soon. If you would like early access, please email PortalSupport@ShiftMed.com'
				)
			);
		}
		return true; // placeholder for sonarcloud
	};

	const handleSwitchToRecurringClick = () => {
		if (requestTabHasContent()) {
			setShowSwitchToRecurringModal(true);
		} else {
			triggerRecurringShiftsModal();
		}
	};

	const handleConfirmSwitchToRecurring = () => {
		setShiftsGroups(initialShiftGroups);
		setSelectedCaregiver(null);
		setShowSwitchToRecurringModal(false);
		triggerRecurringShiftsModal();
	};

	const dismissSwitchToRecurring = () => {
		setShowSwitchToRecurringModal(false);
	};

	const showUnits = useMemo(() => {
		if (!currentFacilityUnits?.length) {
			return false;
		}
		// if the facility doesn't have portal units enabled, permissions don't matter anymore
		if (!selectedFacility?.portal_units_enabled) {
			return false;
		}
		if (isGlobalCalendar) {
			const facilityPermissions = permissionsByFacility[selectedFacility?.id];
			return utils.isAuthorizedExact(
				props.isFlex ? PERMISSION.FLEX_ASSIGN_UNITS : PERMISSION.EDIT_ASSIGNMENT,
				facilityPermissions
			);
		}
		return utils.isAuthorizedExact(
			props.isFlex ? PERMISSION.FLEX_ASSIGN_UNITS : PERMISSION.EDIT_ASSIGNMENT,
			permissions
		);
	}, [permissions, selectedFacility, permissionsByFacility, props.isFlex]);

	const active = isOnRequestTab ? requestSubmit : bulkSubmit;
	const isSubmitting = requestSubmit.isSubmitting || bulkSubmit.isSubmitting;

	const updateCurrentShiftsStructures = (facilityId) => {
		const _structures = shiftStructures
			.filter((structure) => structure.facility_id === facilityId)
			.sort((a, b) => {
				const aFromTime = utils.convertTimeStringToSeconds(a.from_time);
				const bFromTime = utils.convertTimeStringToSeconds(b.from_time);
				if (aFromTime < bFromTime) {
					return -1;
				}
				if (aFromTime > bFromTime) {
					return 1;
				}
				let aToTime = utils.convertTimeStringToSeconds(a.to_time);
				if (aFromTime > aToTime) {
					aToTime += 24 * 3600;
				}
				let bToTime = utils.convertTimeStringToSeconds(b.to_time);
				if (bFromTime > bToTime) {
					bToTime += 24 * 3600;
				}
				if (aToTime < bToTime) {
					return -1;
				}
				if (aToTime > bToTime) {
					return 1;
				}
				return 0;
			});
		setCurrentShiftStructures(_structures);
	};

	const handleChangeFacility = () => {
		reset();
		setSelectedCaregiver(null);
		const facility = props.facilities.find((f) => f.value === facilityToLoad);
		setSelectedFacility(facility);
		updateCurrentShiftsStructures(facilityToLoad);
		setCurrentFacilityUnits(facility.units || []);
		setFacilityToLoad(null);
		setShowChangingFacilityModal(false);
	};

	const handleChangeCaregiver = () => {
		reset();
		setSelectedCaregiver(caregiverToLoad);
		setCaregiverToLoad(null);
		setShowChangingCaregiverModal(false);
	};

	const handleSelectCaregiver = (caregiver) => {
		const currentCaregiverId = selectedCaregiver?.id;
		const nextCaregiverId = caregiver?.id;
		const existChanges = shiftsGroups.some((s) => !!s.resourceType);
		if (currentCaregiverId && currentCaregiverId !== nextCaregiverId && existChanges) {
			setCaregiverToLoad(caregiver);
			setShowChangingCaregiverModal(true);
		} else {
			setSelectedCaregiver(caregiver);
		}
	};

	const handleSelectFacility = (e) => {
		const existChanges = shiftsGroups.some((s) => !!s.resourceType);
		const currentFacility = selectedFacility?.id;
		if (currentFacility && currentFacility !== e.facility && existChanges) {
			setShowChangingFacilityModal(true);
			setFacilityToLoad(e.facility);
		} else {
			const facility = props.facilities.find((f) => f.value === e.facility);
			updateCurrentShiftsStructures(e.facility);
			setCurrentFacilityUnits(facility?.units || []);
			setSelectedFacility(facility);
			setSelectedCaregiver(null);
		}
	};

	useEffect(() => {
		if (props.facilities.length === 1) {
			const [activeFacility] = props.facilities;
			setSelectedFacility(activeFacility);
			setCurrentFacilityUnits(activeFacility.units || []);
			updateCurrentShiftsStructures(activeFacility.value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.facilities]);

	const toggleNeedMoreShifts = () => setNeedMoreShifts(!needMoreShifts);

	const dismissChangingFacility = () => setShowChangingFacilityModal(false);

	const dismissChangingCaregiver = () => {
		setCaregiverToLoad(null);
		setShowChangingCaregiverModal(false);
	};

	const dismissChangingTab = () => {
		setTabToLoad(null);
		setShowChangingTabModal(false);
	};

	return [
		<Modal
			key="select-facility-shift-requests-modal"
			size="xl"
			show={
				!active.confirmation.isOpen &&
				!showChangingFacilityModal &&
				!showChangingCaregiverModal &&
				!showChangingTabModal &&
				!showSwitchToRecurringModal
			}
			aria-labelledby="contained-modal-title-vcenter"
			centered
			className={classNames('select-facility-shift-requests-modal', {
				'select-facility-shift-requests-modal--wide': !isOnRequestTab
			})}
			onHide={props.onClose}
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header closeButton className="modal-request-header">
				<Modal.Title id="contained-modal-title-vcenter" className="request-shift-title">
					<span>{props.title || 'Single shifts'}</span>
					{props.isHybrid && (
						<OverlayTrigger
							className="tooltip-component"
							placement={isMobile ? 'bottom' : 'right'}
							delay={{ show: 100, hide: 400 }}
							overlay={
								<Tooltip
									delay={{ show: 150, hide: 400 }}
									className={classNames('info-facility-tooltip', { 'is-bottom-placement': isMobile })}
								>
									Hybrid shifts are open to both Flex and ShiftMed professionals and are filled on a first come first
									serve basis. If you want to limit which group can claim, you can use the request On Demand or request
									Flex shift options.
								</Tooltip>
							}
						>
							<img className="tooltip-title" src={facilityInfoIcon} alt="info" />
						</OverlayTrigger>
					)}
				</Modal.Title>
				{!isMobile && !props.isFlex && !isGlobalCalendar && !props.isHybrid && !props.isOnCall && (
					<Button className="add-recurring-shifts-button" onClick={handleSwitchToRecurringClick} variant="secondary">
						<RefreshIcon />
						&nbsp; Switch to recurring shifts
					</Button>
				)}
			</Modal.Header>
			<Modal.Body className="request-modal-body">
				{showBulkAssignTab && (
					<div className="select-facility-modal-tabs" role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={isOnRequestTab}
							className={classNames('select-facility-modal-tab', { active: isOnRequestTab })}
							onClick={selectRequestTab}
						>
							Request new shifts
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={!isOnRequestTab}
							className={classNames('select-facility-modal-tab', { active: !isOnRequestTab })}
							onClick={selectBulkTab}
						>
							Add assigned shifts
						</button>
					</div>
				)}
				{isOnRequestTab ? (
					<RequestNewShiftsTab
						isOnCall={props.isOnCall}
						isGlobalCalendar={isGlobalCalendar}
						isFlex={props.isFlex}
						isHybrid={props.isHybrid}
						facilities={props.facilities}
						selectedFacility={selectedFacility}
						handleSelectFacility={handleSelectFacility}
						currentShiftStructures={currentShiftStructures}
						shiftsGroups={shiftsGroups}
						updateShift={updateShift}
						removeShift={removeShift}
						addEmptyShiftGroup={addEmptyShiftGroup}
						errors={errors}
						showUnits={showUnits}
						currentFacilityUnits={currentFacilityUnits}
						maxShiftsToCreate={userData.maxShiftsToCreate}
						checkIfCanAddTodayShiftForStructure={checkIfCanAddTodayShiftForStructure}
						isMobile={isMobile}
						allowPastDates={allowPastDates}
					/>
				) : (
					<BulkAssignShiftsTab
						facilities={props.facilities}
						permissionsByFacility={permissionsByFacility}
						isGlobalCalendar={isGlobalCalendar}
						selectedFacility={selectedFacility}
						onFacilityChange={handleSelectFacility}
						currentShiftStructures={currentShiftStructures}
						shiftsGroups={shiftsGroups}
						updateShift={updateShift}
						removeShift={removeShift}
						addEmptyShiftGroup={addEmptyShiftGroup}
						errors={errors}
						showUnits={showUnits}
						currentFacilityUnits={currentFacilityUnits}
						maxShiftsToCreate={userData.maxShiftsToCreate}
						checkIfCanAddTodayShiftForStructure={checkIfCanAddTodayShiftForStructure}
						isMobile={isMobile}
						selectedCaregiver={selectedCaregiver}
						onCaregiverChange={handleSelectCaregiver}
						overlapErrors={bulkSubmit.overlapErrors}
						getDateConflict={bulkSubmit.getDateConflict}
					/>
				)}
			</Modal.Body>
			<Modal.Footer>
				<div>
					<label className="need-more-shifts">
						<input
							type="checkbox"
							className="custom-checkbox-control"
							value={needMoreShifts}
							checked={needMoreShifts}
							onChange={toggleNeedMoreShifts}
						/>
						Need more shifts? Create another request
					</label>
				</div>
				<div className="buttons">
					<Button className="cancel-shifts-button" onClick={props.onClose} variant="secondary">
						{props.cancelLabel || 'Cancel'}
					</Button>
					<Button
						variant="primary"
						className="req-shifts-button"
						disabled={active.submitDisabled || !selectedFacility}
						onClick={active.submit}
					>
						{active.confirmLabel}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>,

		<RequestShiftsConfirmation
			key="request-shifts-confirmation"
			isOpen={requestSubmit.confirmation.isOpen}
			variant={requestSubmit.confirmation.variant}
			onConfirm={requestSubmit.confirmation.onConfirm}
			onCancel={requestSubmit.confirmation.onCancel}
			isSubmitting={requestSubmit.isSubmitting}
			selectedFacility={selectedFacility}
			shiftsGroups={shiftsGroups}
			shiftStructures={shiftStructures}
			currentFacilityUnits={currentFacilityUnits}
			showUnits={showUnits}
			isHybrid={props.isHybrid}
			isOnCall={props.isOnCall}
		/>,

		<BulkAssignConfirmation
			key="bulk-assign-confirmation"
			isOpen={bulkSubmit.confirmation.isOpen}
			variant={bulkSubmit.confirmation.variant}
			unavailableDates={bulkSubmit.confirmation.unavailableDates}
			onConfirm={bulkSubmit.confirmation.onConfirm}
			onCancel={bulkSubmit.confirmation.onCancel}
			isSubmitting={bulkSubmit.isSubmitting}
			professionalLabel={selectedCaregiver?.displayLabel || selectedCaregiver?.name}
			facilityLabel={selectedFacility?.label}
			shiftsGroups={shiftsGroups}
			shiftStructures={currentShiftStructures}
			currentFacilityUnits={currentFacilityUnits}
			facilityTimezone={selectedFacility?.timezone}
			showUnits={showUnits}
		/>,

		showChangingFacilityModal && (
			<ConfirmationModalNew
				key="change-facility-confirmation-modal"
				className="change-facility-confirmation-modal"
				title="Change facility?"
				confirmLabel="Change facility"
				onConfirm={handleChangeFacility}
				cancelLabel="Go back"
				onCancel={dismissChangingFacility}
				showLoadingSpinner={isSubmitting}
			>
				<>
					<p>Your shift request has not been completed.</p>
					<div className="change-facility-secondary-text">
						If you switch to a different facility now, your current shift request <strong>will be discarded</strong>.
					</div>
				</>
			</ConfirmationModalNew>
		),

		showChangingCaregiverModal && (
			<ConfirmationModalNew
				key="change-caregiver-confirmation-modal"
				className="change-facility-confirmation-modal"
				title="Change professional?"
				confirmLabel="Change professional"
				onConfirm={handleChangeCaregiver}
				cancelLabel="Go back"
				onCancel={dismissChangingCaregiver}
				showLoadingSpinner={isSubmitting}
			>
				<>
					<p>Your shift request has not been completed.</p>
					<div className="change-facility-secondary-text">
						If you change professionals now, your current request <strong>will be discarded</strong>.
					</div>
				</>
			</ConfirmationModalNew>
		),

		showChangingTabModal && (
			<ConfirmationModalNew
				key="change-tab-confirmation-modal"
				className="change-facility-confirmation-modal"
				title={tabToLoad === TAB_REQUEST_NEW_SHIFTS ? 'Request new shifts instead?' : 'Add assigned shifts instead?'}
				confirmLabel="Continue"
				onConfirm={handleChangeTab}
				cancelLabel="Go back"
				onCancel={dismissChangingTab}
				showLoadingSpinner={isSubmitting}
			>
				<>
					<p>Your shift request has not been completed.</p>
					<div className="change-facility-secondary-text">
						If you switch tabs now, your current shift request <strong>will be discarded</strong>.
					</div>
				</>
			</ConfirmationModalNew>
		),

		showSwitchToRecurringModal && (
			<ConfirmationModalNew
				key="switch-to-recurring-confirmation-modal"
				className="change-facility-confirmation-modal"
				title="Request recurring shifts instead?"
				confirmLabel="Continue"
				onConfirm={handleConfirmSwitchToRecurring}
				cancelLabel="Go back"
				onCancel={dismissSwitchToRecurring}
				showLoadingSpinner={isSubmitting}
			>
				<>
					<p>Your shift request has not been completed.</p>
					<div className="change-facility-secondary-text">
						If you switch tabs now, your current shift request <strong>will be discarded</strong>.
					</div>
				</>
			</ConfirmationModalNew>
		)
	];
};

export default memo(SelectFacilityRequestShiftsModal);
